import { StreamLanguage } from "@codemirror/language";

// Qlik expression functions (primary ones used in chart expressions)
const QLIK_FUNCTIONS = new Set([
  // Aggregation
  "Sum",
  "Count",
  "Avg",
  "Min",
  "Max",
  "Mode",
  "Median",
  "Only",
  "Concat",
  "FirstSortedValue",
  "Last",
  "First",
  "MinString",
  "MaxString",
  "RangeSum",
  "RangeAvg",
  "RangeMin",
  "RangeMax",
  "RangeCount",
  "RangeMode",
  "RangeMedian",
  "RangeOnly",
  "RangeStdev",
  "RangeSkew",
  "RangeKurtosis",
  "RangeCorrel",
  "RangeIRR",
  "RangeNPV",
  "RangeXIRR",
  "RangeXNPV",
  // Inter-record / chart
  "Aggr",
  "Above",
  "Below",
  "Before",
  "After",
  "Top",
  "Bottom",
  "Column",
  "Row",
  "RowNo",
  "ColNo",
  "Dimensionality",
  "NoOfRows",
  "NoOfColumns",
  "SecondaryDimensionality",
  // Conditional
  "If",
  "Alt",
  "Pick",
  "Match",
  "WildMatch",
  "MixMatch",
  "Coalesce",
  "Class",
  // String
  "Left",
  "Right",
  "Mid",
  "Len",
  "Trim",
  "LTrim",
  "RTrim",
  "Upper",
  "Lower",
  "Proper",
  "SubField",
  "Replace",
  "Index",
  "FindOneOf",
  "KeepChar",
  "PurgeChar",
  "Repeat",
  "TextBetween",
  "Capitalize",
  "Evaluate",
  "Hash128",
  "Hash160",
  "Hash256",
  "Chr",
  "Ord",
  // Numeric / Math
  "Num",
  "Floor",
  "Ceil",
  "Round",
  "Fabs",
  "Frac",
  "Sign",
  "Sqrt",
  "Sqr",
  "Pow",
  "Exp",
  "Log",
  "Log10",
  "Mod",
  "Div",
  "Odd",
  "Even",
  "Fact",
  "Combin",
  "Permut",
  "Pi",
  "Rand",
  "Sin",
  "Cos",
  "Tan",
  "Asin",
  "Acos",
  "Atan",
  "Atan2",
  "Sinh",
  "Cosh",
  "Tanh",
  "Degrees",
  "Radians",
  // Date/Time
  "Date",
  "Time",
  "Timestamp",
  "Now",
  "Today",
  "Year",
  "Month",
  "Day",
  "Hour",
  "Minute",
  "Second",
  "Week",
  "Weekday",
  "WeekYear",
  "YearToDate",
  "InYear",
  "InYearToDate",
  "InQuarter",
  "InQuarterToDate",
  "InMonth",
  "InMonthToDate",
  "InWeek",
  "InWeekToDate",
  "InDay",
  "InDayToHour",
  "MakeDate",
  "MakeTime",
  "MakeWeekDate",
  "QuarterStart",
  "QuarterEnd",
  "MonthStart",
  "MonthEnd",
  "WeekStart",
  "WeekEnd",
  "YearStart",
  "YearEnd",
  "DayStart",
  "DayEnd",
  "AddMonths",
  "AddYears",
  "NetworkDays",
  "Age",
  "Birthday",
  "ConvertToLocalTime",
  "DaylightSaving",
  "GMT",
  "LocalTime",
  "TimeZone",
  "UTC",
  "DateAdd",
  "DateDiff",
  // Color
  "RGB",
  "ARGB",
  "HSL",
  "ColorMix1",
  "ColorMix2",
  "Darker",
  "Lighter",
  "Color",
  "ColorMapHue",
  // Statistical
  "Stdev",
  "Skew",
  "Kurtosis",
  "Correl",
  "Fractile",
  "NTile",
  "IRR",
  "NPV",
  "XIRR",
  "XNPV",
  "Linest_b",
  "Linest_m",
  "Linest_r2",
  "Linest_sem",
  "Linest_seb",
  "Linest_sey",
  "Linest_df",
  "Linest_f",
  "Linest_ssreg",
  "Linest_ssresid",
  "Normdist",
  "Norminv",
  // Mapping
  "ApplyMap",
  "MapSubstring",
  "LookUp",
  // NULL / type check
  "IsNull",
  "IsNum",
  "IsText",
  "IsPartialReload",
  "Null",
  "Dual",
  // Variable / document
  "Variable",
  "GetExtendedProperty",
  "GetObjectField",
  "FieldValueCount",
  "FieldIndex",
  "FieldValue",
  "GetAlternativeCount",
  "GetExcludedCount",
  "GetNotSelectedCount",
  "GetPossibleCount",
  "GetSelectedCount",
  "GetCurrentSelections",
  "GetFieldSelections",
  "Selection",
  "GetObjectDimension",
  "GetObjectMeasure",
]);

const QLIK_KEYWORDS = new Set([
  "AND",
  "OR",
  "NOT",
  "LIKE",
  "IN",
  "IS",
  "NULL",
  "TOTAL",
  "DISTINCT",
  "ALL",
  "ABOVE",
  "BELOW",
  "AS",
  "BY",
  "SET",
  "LET",
  "IF",
  "THEN",
  "ELSE",
  "END",
  "TRUE",
  "FALSE",
]);

export const qlikExpressionLanguage = StreamLanguage.define({
  name: "qlik-expression",

  token(stream) {
    // Skip whitespace
    if (stream.eatSpace()) return null;

    const ch = stream.peek();

    // Line comment
    if (stream.match("//")) {
      stream.skipToEnd();
      return "comment";
    }

    // Block comment
    if (stream.match("/*")) {
      let found = false;
      while (!stream.eol()) {
        if (stream.match("*/")) {
          found = true;
          break;
        }
        stream.next();
      }
      if (!found) {
        // multi-line block comment — mark rest of line
      }
      return "comment";
    }

    // Dollar-sign expansion: $(variableName) or $(=expression)
    if (stream.match("$(")) {
      // consume until closing )
      let depth = 1;
      while (!stream.eol() && depth > 0) {
        const c = stream.next();
        if (c === "(") depth++;
        else if (c === ")") depth--;
      }
      return "variableName.special";
    }

    // Set analysis opening brace
    if (ch === "{") {
      stream.next();
      return "bracket";
    }
    if (ch === "}") {
      stream.next();
      return "bracket";
    }
    if (ch === "<" && stream.match("<", false)) {
      stream.next();
      return "bracket";
    }
    if (ch === ">") {
      // could be set analysis closing delimiter or operator
      stream.next();
      return "bracket";
    }

    // Single-quoted string
    if (ch === "'") {
      stream.next();
      while (!stream.eol()) {
        const c = stream.next();
        if (c === "'") {
          if (stream.peek() === "'") {
            // escaped quote
            stream.next();
          } else {
            break;
          }
        }
      }
      return "string";
    }

    // Double-quoted string (field name)
    if (ch === '"') {
      stream.next();
      while (!stream.eol()) {
        const c = stream.next();
        if (c === '"') break;
      }
      return "string.special";
    }

    // Bracket-quoted field name [Field Name]
    if (ch === "[") {
      stream.next();
      while (!stream.eol()) {
        const c = stream.next();
        if (c === "]") break;
      }
      return "string.special";
    }

    // Numbers
    if (ch != null && /[0-9]/.test(ch)) {
      stream.match(/^[0-9]*\.?[0-9]+([eE][+-]?[0-9]+)?/);
      return "number";
    }

    // Operators
    if (ch != null && /[+\-*/=<>!%&|^~]/.test(ch)) {
      stream.match(/^[+\-*/=<>!%&|^~]+/);
      return "operator";
    }

    // Parentheses and commas
    if (ch === "(" || ch === ")" || ch === ",") {
      stream.next();
      return "punctuation";
    }

    // Identifiers: functions, keywords, field names
    if (ch != null && /[a-zA-Z_]/.test(ch)) {
      stream.match(/^[a-zA-Z_][a-zA-Z0-9_]*/);
      const word = stream.current();
      const upperWord = word.toUpperCase();

      if (QLIK_FUNCTIONS.has(word) || QLIK_FUNCTIONS.has(upperWord)) {
        return "function";
      }
      if (QLIK_KEYWORDS.has(upperWord)) {
        return "keyword";
      }
      return "name";
    }

    // Fallback — consume one character
    stream.next();
    return null;
  },
});
